import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import Picture from "../Picture/Picture.jsx";
import styles from "./Hero.module.css";

// Slightly faster than real time: it covers more of the reel in the seconds a visitor
// actually watches, without re-encoding the file. 1.2 since the 2026-09-26 reel, whose
// dashboard and model shots felt rushed at 1.5.
const PLAYBACK_RATE = 1.2;

/**
 * Full-bleed hero. `poster` is a Picture base path; `video` is { mp4?, mp4Wide?, webm? } of a
 * silent loop. `mp4Wide` is a higher-resolution cut for screens 48rem and wider; phones, and
 * browsers that ignore <source media>, fall through to `mp4`.
 * The video is hidden under prefers-reduced-motion and the panel's reduced-motion class.
 * `stats` ([{ value, label }]) docks a row of figures along the bottom edge of the frame.
 */
export default function Hero({ eyebrow, title, lead, primary, secondary, poster, video, stats }) {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  // The video fades in over the poster once it is really playing, rather than cutting in
  // (or flashing its own poster frame) while it loads.
  const [isShowing, setIsShowing] = useState(false);

  // On mount too, not just onLoadedMetadata: the prerendered <video> starts loading before
  // React hydrates, so that event has usually already fired by the time the handler exists.
  // defaultPlaybackRate as well, since a load resets playbackRate back to it.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.defaultPlaybackRate = PLAYBACK_RATE;
    el.playbackRate = PLAYBACK_RATE;
    const show = () => setIsShowing(true);
    el.addEventListener("playing", show);
    // It may already be playing: the prerendered <video> can start before React hydrates.
    const frame = requestAnimationFrame(() => {
      if (!el.paused && el.readyState >= 3) show();
    });
    return () => {
      el.removeEventListener("playing", show);
      cancelAnimationFrame(frame);
    };
  }, []);

  const togglePlayback = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) {
      el.playbackRate = PLAYBACK_RATE;
      el.play();
      setIsPlaying(true);
    } else {
      el.pause();
      setIsPlaying(false);
    }
  };

  return (
    <section className={styles.hero} aria-labelledby="hero-title">
      <div className={styles.media}>
        <Picture
          src={poster}
          alt=""
          priority
          widths={[960, 1600]}
          sizes="100vw"
          className={styles.poster}
          aria-hidden="true"
        />
        {video ? (
          // Decorative, silent background loop (no dialogue/audio track); it duplicates
          // the poster image content, which already carries alt="". jsx-a11y's
          // media-has-caption rule does not flag this element (verified via `npm run
          // lint`), so no inline disable is needed here.
          <video
            ref={videoRef}
            className={`${styles.video} ${isShowing ? styles.videoShowing : ""}`.trim()}
            autoPlay
            muted
            loop
            playsInline
            disablePictureInPicture
            aria-hidden="true"
            preload="metadata"
            poster={`${poster}-1600.jpg`}
            onLoadedMetadata={(e) => {
              e.currentTarget.playbackRate = PLAYBACK_RATE;
            }}
          >
            {video.mp4Wide ? (
              <source src={video.mp4Wide} type="video/mp4" media="(min-width: 48rem)" />
            ) : null}
            {video.webm ? <source src={video.webm} type="video/webm" /> : null}
            {video.mp4 ? <source src={video.mp4} type="video/mp4" /> : null}
          </video>
        ) : null}
        <div className={styles.overlay} aria-hidden="true" />
        {video ? (
          <button
            type="button"
            className={styles.playPause}
            onClick={togglePlayback}
            aria-label={isPlaying ? "Pause background video" : "Play background video"}
          >
            <span className={isPlaying ? styles.pauseIcon : styles.playIcon} aria-hidden="true" />
          </button>
        ) : null}
      </div>
      {/* Outside `.media` on purpose: `.media` is absolute, so text inside it could not grow the
          hero, and a phone's wrapped headline pushed the buttons past its `overflow: hidden`. */}
      <div className={`container ${styles.content}`}>
        {eyebrow ? <p className={styles.eyebrow}>{eyebrow}</p> : null}
        <h1 id="hero-title" className={styles.title}>
          {title}
        </h1>
        <p className={styles.lead}>{lead}</p>
        <div className={styles.actions}>
          <Link to={primary.to} className="btn btn-primary">
            {primary.label}
          </Link>
          {secondary ? (
            <Link to={secondary.to} className="btn btn-secondary">
              {secondary.label}
            </Link>
          ) : null}
        </div>
      </div>
      {stats ? (
        <div className={styles.statsBar}>
          <ul className={`container ${styles.stats}`} aria-label="Key figures">
            {stats.map((s) => (
              <li key={s.label} className={styles.stat}>
                <span className={styles.statValue}>{s.value}</span>
                <span className={styles.statLabel}>{s.label}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
